import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import {
  BadgeCell,
  CurrencyCell,
  DimensionsCell,
  HtmlCell,
  TextCell,
} from "@/components/table/CustomCell";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { selectAttachmentForPreview, TypeDefaultImage } from "@/lib/image";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";
import { isSculptureModuleEnabled } from "@/lib/compute-field/artwork-module-helpers";
import { t as globalT, TFunction } from "i18next";

export interface ColumnFormatted<T = any, TContext = any> {
  type: string;
  key: string;
  visible?: boolean;
  formattedData?: (data: T, context?: TContext) => string;
}

export const getFormattedContent = <TContext = any,>(
  data: Record<string, unknown>,
  config?: ColumnFormatted[],
  col?: string,
  context?: TContext,
): string | undefined => {
  if (!config && (!col || !(col in data))) {
    return undefined;
  }

  if (!config && col && col in data) return String(data[col]);

  if (!config || !col) return undefined;

  const relatedConfig = config.find((c) => c.key === col);

  if (!relatedConfig) {
    return undefined;
  }

  if (!relatedConfig?.formattedData) return String(data[col]);

  return relatedConfig?.formattedData(data, context);
};

function multilingualPlainText(
  value: unknown,
  locale: SupportedLocale,
): string {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }
  const raw = (value as Record<string, unknown>)[locale];
  return raw == null ? "" : String(raw);
}

function containsHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function resolveAttachmentProfile(
  domain: TypeDefaultImage,
  rowData: Record<string, unknown>,
  context?: unknown,
): TypeDefaultImage {
  if (domain !== "artwork" || !context || typeof context !== "object") {
    return domain;
  }

  const locale = getMainLanguage();
  const isSculptureArtwork = isSculptureModuleEnabled(
    rowData as any,
    context as any,
    locale as SupportedLocale,
  );

  return isSculptureArtwork ? "sculptureEdition" : domain;
}

export const generateTable = <TContext = any,>(
  columns: ColumnFormatted[],
  domain: TypeDefaultImage,
  localT?: TFunction,
  context?: TContext,
) => {
  const t = localT ?? globalT;
  return columns.map((column: ColumnFormatted) => ({
    accessorKey: column.key,
    meta: { defaultVisible: column.visible ?? true },
    header: column.type !== "image" ? t(`tab_${column.key}`) : "",
    cell: (props: any) => {
      if (column.type === "image") {
        const attachments = props.row.original.attachments ?? [];
        const attachmentProfile = resolveAttachmentProfile(
          domain,
          props.row.original,
          context,
        );
        const previewAttachment = selectAttachmentForPreview(
          attachments,
          attachmentProfile,
        );
        const thumbUrl =
          previewAttachment && previewAttachment.isGenericImage !== true
            ? previewAttachment.kind === "embed"
              ? resolveEmbedThumbnailUrl(
                  previewAttachment.embedProvider,
                  previewAttachment.embedVideoId,
                  previewAttachment.embedUrl,
                ) ?? null
              : previewAttachment.kind === "video"
                ? previewAttachment.posterPublicUrl ||
                    previewAttachment.mediumPublicUrl ||
                    previewAttachment.publicUrl ||
                    previewAttachment.largePublicUrl ||
                    null
                : previewAttachment.mediumPublicUrl ||
                    previewAttachment.publicUrl ||
                    previewAttachment.largePublicUrl ||
                    null
            : null;
        const canRender =
          !!previewAttachment &&
          (previewAttachment.isGenericImage === true ||
            !!thumbUrl ||
            (previewAttachment.kind === "video" &&
              !!previewAttachment.videoPublicUrl) ||
            previewAttachment.kind === "embed");
        if (!canRender) {
          return <div className="flex items-center gap-3 text-wrap" />;
        }
        return (
          <div
            className="inline-block"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <ImageFullscreen
              media={{
                attachments,
                type: attachmentProfile,
                alt: t("image"),
                width: 70,
                height: 70,
                className: "size-[70px] aspect-square object-contain",
              }}
            />
          </div>
        );
      } else if (column.type === "boolean") {
        return (
          <BadgeCell
            value={props.getValue() ? t("oui") : t("non")}
            variant={props.getValue() ? "published" : "draft"}
          />
        );
      } else if (column.type === "status") {
        return (
          <BadgeCell
            value={
              props.getValue() === "published" ? t("published") : t("draft")
            }
            variant={props.getValue() === "published" ? "published" : "draft"}
          />
        );
      } else if (column.type === "richtext") {
        return <HtmlCell value={props.getValue()} />;
      } else if (column.type === "string") {
        return <TextCell value={props.getValue()} />;
      } else if (column.type === "dimensions") {
        return (
          <DimensionsCell
            value={props.getValue()}
            row={props.row.original as Record<string, unknown>}
            dimensionKey={column.key}
          />
        );
      } else if (column.type === "computed") {
        const formattedData = column.formattedData
          ? column.formattedData(props.row.original, context)
          : null;
        const value = formattedData ?? "";
        const isPlainText = typeof value === "string" && !value.includes("<");
        return isPlainText ? (
          <TextCell value={value} />
        ) : (
          <HtmlCell value={value} />
        );
      } else if (column.type === "currency") {
        return <CurrencyCell value={props.getValue()} />;
      } else if (column.type === "multilingual") {
        const localizedValue = multilingualPlainText(
          props.getValue(),
          getMainLanguage() as SupportedLocale,
        );
        const shouldRenderHtml = containsHtml(localizedValue);
        return shouldRenderHtml ? (
          <HtmlCell value={localizedValue} />
        ) : (
          <TextCell value={localizedValue} />
        );
      } else if (column.type === "badge") {
        const formattedData = column.formattedData
          ? column.formattedData(props.row.original, context)
          : props.getValue();
        return <BadgeCell value={formattedData} variant="outlined" />;
      } else {
        return;
      }
    },
  }));
};
